import { NATIVE_COMMERCE_BOOT_SCRIPT } from "@/lib/native-app";
import { installJourneyReturnTracking, onRouteChangeForJourney } from "@/lib/journey-return";
import { installStaleBuildCheck } from "@/lib/stale-build";
import { localeTo } from "@/lib/locale";
import { afterCheckout } from "@/lib/checkout-lock";
import { OfflineNotice } from "@/components/OfflineNotice";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useLocation,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { registerAppServiceWorker, clearOfflinePages } from "@/lib/pwa";
import { installAudioBus, stopAllAudio } from "@/lib/audio-bus";

import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";
import { Toaster } from "@/components/ui/sonner";
import { GlobalMiniPlayer } from "@/components/GlobalMiniPlayer";
import { TtsErrorToast } from "@/components/TtsErrorToast";
import { FeatureBoundary, SilentBoundary } from "@/components/FeatureBoundary";

import { AccountWaitingBanner } from "@/components/membership/AccountWaitingBanner";
import { ReturnToSanctuary } from "@/components/sanctuary/ReturnToSanctuary";
import { UpgradeMembershipPopup } from "@/components/membership/UpgradeMembershipPopup";
import { enforceEphemeralSessionOnBoot } from "@/lib/remember-me";
// Runs at module load — BEFORE any component reads the stored session — so a
// session is never cleared after the membership check has already read it.
enforceEphemeralSessionOnBoot();
import { reportFlagError } from "@/lib/feature-flags";
import { installGlobalErrorReporting } from "@/lib/report-problem";
import { startErrorReporting } from "@/lib/sentry-init";

import { LanguageProvider } from "@/lib/language";


import appCss from "../styles.css?url";

function NotFoundComponent() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

import { Header } from "@/components/Header";

import { BottomNav } from "@/components/BottomNav";
import { NativeShell } from "@/components/native/NativeShell";
import { useIsNativeApp } from "@/lib/native-app";
import { BackButton } from "@/components/BackButton";
import { SectionTitleBar } from "@/components/SectionTitleBar";
import { DisclaimerGate } from "@/components/DisclaimerGate";
// DeviceGuard import removed for now — the device limit popup is disabled (2026-09-21)
import { FooterDisclaimer } from "@/components/FooterDisclaimer";
import { PageSafetyNotice } from "@/components/PageSafetyNotice";

import { CookieConsent } from "@/components/CookieConsent";
import { GlobalFeatureFeedback } from "@/components/GlobalFeatureFeedback";
import { RecentActivityTracker } from "@/components/RecentActivityTracker";
import { VisitorTracker } from "@/components/VisitorTracker";
import { ProviderReturnTracker } from "@/components/ProviderReturnTracker";
import { NavigationScrollReset } from "@/components/NavigationScrollReset";
import { LaunchLockGate } from "@/components/LaunchLockGate";
import { GroundMeNow } from "@/components/GroundMeNow";
import { SupportChatWidget } from "@/components/SupportChatWidget";
import { GuestLockModal } from "@/components/guest/GuestLockModal";
import {
  DisplaySettingsProvider,
  DISPLAY_BOOT_SCRIPT,
} from "@/components/DisplaySettings";
import { useTherapistOnlyMode } from "@/lib/launch-mode";


export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes, viewport-fit=cover" },
      { title: "Recovery Trauma™ | Trauma Recovery Tools, Community & Support" },
      {
        name: "description",
        content:
          "Trauma-informed tools, journaling, community and live events to support your recovery journey with Recovery Trauma™.",
      },
      { name: "author", content: "Recovery Trauma™" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { property: "og:site_name", content: "Recovery Trauma™" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { property: "og:title", content: "Recovery Trauma™ | Trauma Recovery Tools, Community & Support" },
      {
        property: "og:description",
        content:
          "Trauma-informed tools, journaling, community and live events to support your recovery journey.",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Recovery Trauma™ | Trauma Recovery Tools, Community & Support" },
      {
        name: "twitter:description",
        content:
          "Trauma-informed tools, journaling, community and live events to support your recovery journey.",
      },
      { property: "og:image", content: "https://recoverytrauma.com/__l5e/assets-v1/30e63f8c-e96a-4d0d-b4d8-eea84ba8ec57/recovery-trauma-og.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Recovery Trauma™ — Healing Beyond Survival Mode™" },
      { name: "twitter:image", content: "https://recoverytrauma.com/__l5e/assets-v1/30e63f8c-e96a-4d0d-b4d8-eea84ba8ec57/recovery-trauma-og.jpg" },
      { name: "theme-color", content: "#faf6ef" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "Recovery Trauma" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Figtree:wght@400;600&family=Fredoka:wght@500;600;700&family=Outfit:wght@600;700&family=Quicksand:wght@400;500;600;700&display=swap",
      },
    ],
    scripts: [
      { children: DISPLAY_BOOT_SCRIPT },
      { children: NATIVE_COMMERCE_BOOT_SCRIPT },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Recovery Trauma™",
          alternateName: "Recovery Trauma™",
          url: "https://recoverytrauma.com",
          logo: "https://recoverytrauma.com/favicon.ico",
          description:
            "Trauma-informed educational and wellness platform offering self-help tools, courses, community, and live events to support recovery.",
          sameAs: [
            "https://www.instagram.com/recoverytrauma_",
            "https://www.facebook.com/people/Recovery-Trauma/61568168060890",
            "https://www.pinterest.com/recoverytrauma",
            "https://www.tiktok.com/@recoverytrauma",
            "https://www.youtube.com/@RecoveryTrauma",
          ],
          foundingDate: "2024",
          founder: {
            "@type": "Person",
            name: "Jana Burkhardt",
            jobTitle: "Founder",
            description:
              "Survivor and founder of Recovery Trauma\u2122, writing from lived experience of trauma recovery.",
            url: "https://recoverytrauma.com/about",
          },
          knowsAbout: [
            "Complex PTSD (CPTSD)",
            "PTSD",
            "Trauma recovery",
            "Nervous system regulation",
            "Polyvagal theory",
            "Window of tolerance",
            "Internal Family Systems (IFS)",
            "Emotional flashbacks",
            "Trauma bonding",
            "Hypervigilance",
            "Attachment styles",
            "Boundaries",
            "Grounding techniques",
            "EFT tapping",
            "CBT and DBT skills",
            "Somatic practices",
            "Shadow work",
            "Trauma-informed journaling",
          ],
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer support",
            url: "https://recoverytrauma.com/support",
            availableLanguage: ["English", "German", "French", "Spanish"],
          },

        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Recovery Trauma™",
          url: "https://recoverytrauma.com",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://recoverytrauma.com/library?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const { pathname } = useLocation();
  useEffect(() => installJourneyReturnTracking(), []);
  useEffect(() => installStaleBuildCheck(), []);
  const prevPathRef = useRef<string | null>(null);
  useEffect(() => {
    onRouteChangeForJourney(pathname, prevPathRef.current);
    prevPathRef.current = pathname;
  }, [pathname]);
  const nativeApp = useIsNativeApp();
  const inSanctuary = pathname === "/sanctuary";
  const hideFooter = pathname === "/" || inSanctuary;
  // The paid signup journey stays free of overlays: nothing should sit
  // between someone deciding to join and the payment page.
  const conversionFlow = [
    "/choose-membership",
    "/signup",
    "/complete-membership",
    "/checkout",
  ].some((p) => pathname === p || pathname.startsWith(`${p}/`));

  const therapistOnly = useTherapistOnlyMode();

  return (
    <QueryClientProvider client={queryClient}>
      <DisplaySettingsProvider>
        <LanguageProvider>
        <AuthSync />
        {/* Device limit popup removed for now (Jana, 2026-09-21) — device
            tracking itself still runs; only the "Device limit reached"
            overlay was taken out. Manage devices at /account/devices. */}
        <SilentBoundary feature="trackers">
          <RecentActivityTracker />
          <VisitorTracker />
          <ProviderReturnTracker />
        </SilentBoundary>
        <NavigationScrollReset />

        <div className="rt-shell flex min-h-dvh flex-col bg-background">
          {!inSanctuary && <SilentBoundary feature="header"><Header /></SilentBoundary>}
          
          {!inSanctuary && <SilentBoundary feature="section-title-bar"><SectionTitleBar /></SilentBoundary>}
          {!inSanctuary && <SilentBoundary feature="back-button"><BackButton /></SilentBoundary>}
          <SilentBoundary feature="account-waiting"><AccountWaitingBanner /></SilentBoundary>
          <SilentBoundary feature="sanctuary-return"><ReturnToSanctuary /></SilentBoundary>
          <main className={inSanctuary ? "flex-1" : hideFooter ? "flex-1 pb-[calc(env(safe-area-inset-bottom)+9rem)] lg:pb-12" : "flex-1 pb-4 lg:pb-6"}>
            <LaunchLockGate>
              <FeatureBoundary feature="page-content" label="This part of the page">
                <Outlet />
              </FeatureBoundary>
            </LaunchLockGate>

            {!inSanctuary && <SilentBoundary feature="page-safety-notice"><PageSafetyNotice /></SilentBoundary>}
            {!inSanctuary && <SilentBoundary feature="feature-feedback"><GlobalFeatureFeedback /></SilentBoundary>}
          </main>
          {!hideFooter && (
            <SilentBoundary feature="footer"><FooterDisclaimer /></SilentBoundary>
          )}
          {!conversionFlow && (
            <SilentBoundary feature="disclaimer-gate"><DisclaimerGate /></SilentBoundary>
          )}

          {!conversionFlow && (
            <SilentBoundary feature="ground-me-now"><GroundMeNow /></SilentBoundary>
          )}
          {!conversionFlow && (
            <SilentBoundary feature="support-chat"><SupportChatWidget /></SilentBoundary>
          )}
          {!conversionFlow && (
            <SilentBoundary feature="cookie-consent"><CookieConsent /></SilentBoundary>
          )}
          {/* EmailCapturePrompt removed — replaced by non-blocking inline EmailCapture on key pages */}
          <SilentBoundary feature="mini-player"><GlobalMiniPlayer /></SilentBoundary>
          <SilentBoundary feature="offline-notice"><OfflineNotice /></SilentBoundary>
          <SilentBoundary feature="native-shell"><NativeShell /></SilentBoundary>
          {!therapistOnly && !nativeApp && !inSanctuary && (
            <SilentBoundary feature="bottom-nav"><BottomNav /></SilentBoundary>
          )}
<Toaster richColors position="top-center" />
          <SilentBoundary feature="tts-error-toast"><TtsErrorToast /></SilentBoundary>
          <SilentBoundary feature="guest-lock-modal"><GuestLockModal /></SilentBoundary>
          <SilentBoundary feature="upgrade-popup"><UpgradeMembershipPopup /></SilentBoundary>
          

          
        </div>
        </LanguageProvider>
      </DisplaySettingsProvider>
    </QueryClientProvider>
  );
}

function AuthSync() {
  const router = useRouter();
  const queryClient = useQueryClient();
  // One global audio slot: starting any track stops whatever was playing.
  useEffect(() => {
    installAudioBus();
  }, []);

  // Leaving a page stops anything still making sound there.
  useEffect(() => {
    const unsub = router.subscribe("onBeforeNavigate", () => stopAllAudio({ keepPersistent: true }));
    return unsub;
  }, [router]);


  // Installable app / offline fallback. Guarded: never registers in dev or
  // Lovable preview, and unregisters any stale worker there.
  useEffect(() => {
    registerAppServiceWorker();
  }, []);

  // Feature-flag kill-switch: report every unhandled JS error / promise
  // rejection with the flag context that was active when it fired. The
  // hourly monitor uses these to auto-disable any flag whose 15-min error
  // rate exceeds 2× its 24h baseline (≥5 errors floor).
  useEffect(() => {
    const onError = (e: ErrorEvent) => reportFlagError({ error: e.error ?? e.message });
    const onRejection = (e: PromiseRejectionEvent) => reportFlagError({ error: e.reason });
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    // Same events also feed the admin Issues inbox (noise-filtered, capped,
    // and grouped by fingerprint server-side).
    const stopIssueReporting = installGlobalErrorReporting();
    // Production error reporting (only active once a reporting key is set).
    void startErrorReporting();

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      stopIssueReporting();
    };
  }, []);

  useEffect(() => {
    const safeAppPath = (target?: string | null, fallback = "/dashboard") => {
      if (!target || !target.startsWith("/") || target.startsWith("//")) return fallback;
      return target;
    };

    const clearOAuthState = () => {
      sessionStorage.removeItem("oauth_pending");
      sessionStorage.removeItem("oauth_redirect");
      sessionStorage.removeItem("admin_oauth_pending");
      sessionStorage.removeItem("admin_oauth_redirect");
    };

    const routeOAuthReturn = (session: Session | null) => {
      if (!session?.user?.id || typeof window === "undefined") return;
      const path = window.location.pathname;
      const loginProvider = sessionStorage.getItem("oauth_pending");
      const adminProvider = sessionStorage.getItem("admin_oauth_pending");
      const loginPending = loginProvider === "google" || loginProvider === "apple";
      const adminPending = adminProvider === "google" || adminProvider === "apple";
      console.log("[OAuth][root] routeOAuthReturn", {
        path,
        userId: session.user.id,
        email: session.user.email,
        provider: session.user.app_metadata?.provider,
        loginPending,
        adminPending,
        href: window.location.href,
      });
      if (path !== "/" && path !== "/login" && path !== "/admin-login") return;
      if (!loginPending && !adminPending) return;

      const loginRedirect = sessionStorage.getItem("oauth_redirect");
      const adminRedirect = sessionStorage.getItem("admin_oauth_redirect");
      supabase
        .rpc("has_role", { _user_id: session.user.id, _role: "admin" })
        .then(({ data, error }) => {
          const isAdmin = !error && Boolean(data);
          console.log("[OAuth][root] has_role result", {
            isAdmin,
            rpcError: error?.message,
            loginRedirect,
            adminRedirect,
          });
          if (adminPending && !isAdmin) {
            console.warn("[OAuth][root] admin pending but user is NOT admin — signing out");
            clearOAuthState();
            void supabase.auth.signOut().finally(() => {
              window.location.assign("/admin-login?error=not_admin");
            });
            return;
          }

          const next = adminPending
            ? safeAppPath(adminRedirect, "/admin")
            : safeAppPath(loginRedirect, "/dashboard");
          console.log("[OAuth][root] redirecting to", next);
          clearOAuthState();
          window.location.assign(localeTo(next));
        });
    };

    // Force-redirect any password recovery link to /reset-password,
    // even if the user is already signed in. Supabase puts recovery
    // tokens in the URL hash (#access_token=...&type=recovery) or, on
    // newer flows, as ?code=...&type=recovery in the query string.
    if (typeof window !== "undefined" && window.location.pathname !== "/reset-password") {
      const hash = window.location.hash || "";
      const search = window.location.search || "";
      const isRecovery =
        hash.includes("type=recovery") ||
        new URLSearchParams(search).get("type") === "recovery";
      if (isRecovery) {
        window.location.replace(localeTo("/reset-password") + `${search}${hash}`);
        return;
      }
    }

    void supabase.auth.getSession().then(({ data, error }) => {
      console.log("[OAuth][root] initial getSession", {
        hasSession: !!data.session,
        userId: data.session?.user?.id,
        email: data.session?.user?.email,
        provider: data.session?.user?.app_metadata?.provider,
        error: error?.message,
        href: typeof window !== "undefined" ? window.location.href : null,
      });
      // "Remember me" is owned solely by src/lib/remember-me.ts, enforced
      // once at module load before anything reads the session. Nothing here
      // may sign a member out.
      routeOAuthReturn(data.session);
    });


    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("[OAuth][root] onAuthStateChange", {
        event,
        hasSession: !!session,
        userId: session?.user?.id,
        email: session?.user?.email,
        provider: session?.user?.app_metadata?.provider,
        path: typeof window !== "undefined" ? window.location.pathname : null,
      });
      if (event === "PASSWORD_RECOVERY" && window.location.pathname !== "/reset-password") {
        window.location.assign(localeTo("/reset-password") + `${window.location.search}${window.location.hash}`);
        return;
      }
      // Broadcast every auth event so other hooks/components can react
      // without each instantiating their own Supabase subscriber (multiple
      // subscribers deadlocked the auth LockManager on signup/login).
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("rt-auth-change", { detail: { event, session } }),
        );
      }
      if (event === "SIGNED_OUT") {
        // Saved offline copies can include private member pages, so they are
        // cleared the moment someone signs out.
        void clearOfflinePages();
        queryClient.clear();
      }
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "TOKEN_REFRESHED") {
        // A brand-new member is signed in moments before their payment
        // session is requested. Refreshing the page's data at that instant
        // tears down that request, so the refresh waits until the payment
        // request has finished (see src/lib/checkout-lock.ts).
        afterCheckout(() => {
          if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
          router.invalidate();
        });
      }
      if (event === "SIGNED_IN" || event === "INITIAL_SESSION") {
        // Self-healing admin promotion: server-side RPC grants admin to
        // users whose email is on the immutable allowlist. Runs on every
        // session hydration so admin access survives logout, password
        // reset, and re-signup. Non-allowlisted users are unaffected.
        if (session?.user?.id) {
          supabase
            .rpc("ensure_admin_role")
            .then(({ data, error }) => {
              console.log("[Admin] ensure_admin_role", {
                granted: !!data,
                error: error?.message,
                userId: session.user.id,
              });
              routeOAuthReturn(session);
            });
          // Pull server-side favourites + backfill any localStorage-only rows.
          void import("@/lib/favorites-sync")
            .then((m) => m.syncFavoritesFromServer())
            .catch(() => {});
        } else {
          routeOAuthReturn(session);
        }
      }

      // First-time member welcome: route to onboarding on initial sign-in only.
      // Guard with a session-scoped flag so a stray SIGNED_IN (token refresh,
      // tab focus, LockManager re-fire) never yanks a mid-writing user off a
      // tool page like /journal, /dreams, /guided-journal, etc.
      if (event === "SIGNED_IN" && session?.user?.id && typeof window !== "undefined") {
        const uid = session.user.id;
        const path = window.location.pathname;
        const shouldSkipMemberWelcome = (candidatePath: string) =>
          candidatePath === "/" ||
          candidatePath.startsWith("/login") ||
          candidatePath.startsWith("/member-welcome") ||
          candidatePath.startsWith("/signup") ||
          candidatePath.startsWith("/verify-email") ||
          candidatePath.startsWith("/membership") ||
          candidatePath.startsWith("/checkout") ||
          candidatePath.startsWith("/admin") ||
          candidatePath.startsWith("/reset-password") ||
          candidatePath.startsWith("/auth") ||
          candidatePath.startsWith("/therapist") ||
          candidatePath.startsWith("/events") ||
          candidatePath.startsWith("/journal") ||
          candidatePath.startsWith("/guided-journal") ||
          candidatePath.startsWith("/dreams") ||
          candidatePath.startsWith("/shadow-work") ||
          candidatePath.startsWith("/meditations") ||
          candidatePath.startsWith("/sounds") ||
          candidatePath.startsWith("/bilateral") ||
          candidatePath.startsWith("/tapping") ||
          candidatePath.startsWith("/yoga") ||
          candidatePath.startsWith("/breath") ||
          candidatePath.startsWith("/somatic") ||
          candidatePath.startsWith("/community") ||
          candidatePath.startsWith("/check-in") ||
          candidatePath.startsWith("/profile") ||
          candidatePath.startsWith("/billing") ||
          candidatePath.startsWith("/my-membership") ||
          candidatePath.startsWith("/crisis");
        const skip = shouldSkipMemberWelcome(path);
        try {
          const sessionFlagKey = "rt:member-welcome-checked:" + uid;
          const alreadyChecked = sessionStorage.getItem(sessionFlagKey) === "1";
          const seen =
            localStorage.getItem("rt:member-welcome-seen:" + uid) === "1" ||
            localStorage.getItem("rt:member-welcome-seen") === "1";
          sessionStorage.setItem(sessionFlagKey, "1");
          if (!seen && !skip && !alreadyChecked) {
            // Admins/testers must never be pushed into member onboarding —
            // they need free navigation across the whole app.
            supabase
              .rpc("has_role", { _user_id: uid, _role: "admin" })
              .then(({ data: isAdmin }) => {
                if (isAdmin) return;
                setTimeout(() => {
                  if (shouldSkipMemberWelcome(window.location.pathname)) return;
                  window.location.assign(localeTo("/member-welcome"));
                }, 400);
              });
          }
        } catch {}
      }
    });
    return () => subscription.unsubscribe();
  }, [router, queryClient]);
  return null;
}
